import { useState, useEffect } from 'react';
import { useNavigate, useResolvedPath, Routes, Route, NavigateFunction } from 'react-router-dom';
import { IService, LocalizedTransportationOption, getService } from '../../../../API/GoMoto';
import { GoMotoComponent, GoMotoFunction } from '../../../../API/GoMoto.types';
import { useCart, useCartDispatch, Types, ITransport } from '../../../../context/cart';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { Spinner } from '../../../../controls/Spinner/Spinner';
import { PathName, Appointment_TransportationRoutePaths } from '../../../../helpers/Routes';
import { TransportationOpenProps } from './TransportationOpen.types';
import { PickUpTime } from '../PickUpTime/PickUpTime';
import { TransportationOptions } from '../TransportationOptions/TransportationOptions';

/**
 * Routing component that routes between the transport options component and the pick up time
 * text area component. It also handles general state management for selecting a transport option
 * as well as details about when the user will return if pickup time is enabled.
 *
 * @param onProceed - {@link TransportationOpenProps.onProceed onProceed} description.
 * @param transportOpts - {@link TransportationOpenProps.transportOpts transportOpts} description.
 * @param pickUpTimeEnabled - {@link TransportationOpenProps.pickUpTimeEnabled pickUpTimeEnabled} description.
 */
export const TransportationOpen: GoMotoComponent<TransportationOpenProps> = ({
    onProceed,
    transportOpts,
    pickUpTimeEnabled,
}) => {
    const { transportation } = useCart();
    const { key } = useKioskState();
    const cartDispatch = useCartDispatch();
    const navigate: NavigateFunction = useNavigate();
    const { pathname } = useResolvedPath('.');

    const [loading, setLoading] = useState<boolean>(false);
    const [showPickUpTime, setShowPickUpTime] = useState<boolean>(false);
    const [transportOpt, setTransportOpt] = useState<LocalizedTransportationOption | undefined>(undefined);

    const onTransportationSelected: GoMotoFunction<[LocalizedTransportationOption], Promise<void>> = async (transportOpt) => {
        setShowPickUpTime(false);
        setTransportOpt(transportOpt);

        if (transportOpt) {
            const service: IService | undefined = await getTransportOptService(transportOpt.opCode);
            const selected: ITransport = {
                ...transportation,
                type: transportOpt.type,
                subtype: transportOpt.subtype,
                transportService: service,
                dmsTransportationType: transportOpt.dmsTransportationType,
                opCode: transportOpt.opCode,
            };

            if (transportOpt.showPickupTime && pickUpTimeEnabled) {
                cartDispatch({
                    type: Types.SetTransportation,
                    payload: selected,
                });
                setShowPickUpTime(true);
            }
            else {
                onProceed({
                    ...selected,
                    pickUpTime: undefined,
                });
            }
        }
    };

    const getTransportOptService: GoMotoFunction<[string], Promise<IService | undefined>> = async (opCode) => {
        setLoading(true);
        const service: IService | undefined = opCode ? await getService(key, opCode) : undefined;
        setLoading(false);

        return service;
    };

    // Set the internal state based on the cart.
    useEffect(() => {
        const generateTransportOpts: GoMotoFunction<[void], Promise<void>> = async () => {
            const selected: LocalizedTransportationOption | undefined = transportOpts!.find((transportOpts: LocalizedTransportationOption) => {
                return transportOpts.type === transportation.type && transportOpts.subtype === transportation.subtype;
            });

            setTransportOpt(selected);
        };


        if (!transportOpt && transportOpts) {
            generateTransportOpts();
        }
    }, [transportation, transportOpt, transportOpts]);

    useEffect(() => {
        if (showPickUpTime) {
            navigate(PathName(pathname, Appointment_TransportationRoutePaths.Pickup));
        }

        setShowPickUpTime(false);
    }, [showPickUpTime, navigate, pathname]);

    if (loading) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <Routes>
                <Route
                    path={PathName(Appointment_TransportationRoutePaths.Pickup, '*')}
                    element={
                        <PickUpTime
                            onProceed={({ pickUpTime }) => {
                                if (transportation) {
                                    onProceed({
                                        ...transportation,
                                        pickUpTime,
                                    });
                                }
                            }}
                        />
                    }
                />
                <Route
                    path={''}
                    element={
                        <TransportationOptions
                            transportationOptions={transportOpts!}
                            onProceed={onTransportationSelected}
                        />
                    }
                />
            </Routes>
        );
    }
};